import { z } from 'zod';

export const ViewPageToolName = 'viewPage' as const;
const coordinate = z.number().finite().min(0).max(1000);

export const viewPageArgsSchema = z
  .object({
    documentId: z
      .string()
      .uuid()
      .nullish()
      .describe(
        'Knowledge-base document ID from search results. Supply exactly one of documentId or url.',
      ),
    url: z
      .string()
      .url()
      .nullish()
      .describe(
        'Exact HTTP(S) PDF URL from the user or tool results. Downloaded PDFs may be reused within this conversation for up to five minutes.',
      ),
    page: z
      .number()
      .int()
      .min(1)
      .nullish()
      .describe(
        'One-based PDF page number. Supply exactly one of page or pages.',
      ),
    pages: z
      .array(z.number().int().min(1))
      .min(1)
      .max(5)
      .nullish()
      .describe(
        'Up to five one-based PDF pages to inspect together. Duplicate pages are read once. Supply exactly one of page or pages.',
      ),
    box: z
      .array(coordinate)
      .length(4)
      .transform(value => value as [number, number, number, number])
      .nullish()
      .describe(
        'Optional [x0,y0,x1,y1] crop, 0–1000 coordinates measured from the top-left corner.',
      ),
    scale: z
      .number()
      .finite()
      .min(0.25)
      .max(8)
      .nullish()
      .describe(
        'Optional rendering scale (default 2); JPEG long side is limited to 2000 pixels.',
      ),
  })
  .refine(
    args => Boolean(args.documentId) !== Boolean(args.url),
    'Supply exactly one of documentId or url',
  )
  .refine(
    args => (args.page != null) !== (args.pages != null),
    'Supply exactly one of page or pages',
  )
  .refine(args => !args.box || args.page != null, 'Use page for a cropped view')
  .refine(
    args =>
      !args.box || (args.box[0] < args.box[2] && args.box[1] < args.box[3]),
    'Crop coordinates must be ordered',
  );

export type ViewPageArgs = z.infer<typeof viewPageArgsSchema>;
